/*!
 * Niagara UI Marketplace — Theme interactions
 * Vanilla JS, no build step. Handles nav, cart drawer (AJAX cart API),
 * scroll reveal, animated counters, FAQ accordion, collection filter/search,
 * and product page variant/qty/gallery controls.
 */
(function () {
  'use strict';

  var qs = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var qsa = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  // Reveal-on-scroll starts content at opacity:0, so a single throwing init
  // used to take the whole page down with it — everything after the failure
  // never ran, including the reveal, and the page rendered blank. Each init is
  // isolated: one broken feature degrades to just that feature being missing.
  function safe(name, fn) {
    try {
      fn();
    } catch (e) {
      if (window.console && console.error) console.error('[niagara] ' + name + ' failed:', e);
    }
  }

  // Last-resort net: if anything at all prevented the reveal from running,
  // show the content anyway rather than leaving a blank page behind.
  function forceReveal() {
    qsa('.reveal, .reveal-scale').forEach(function (el) { el.classList.add('is-visible'); });
  }

  document.addEventListener('DOMContentLoaded', function () {
    safe('scrollReveal', initScrollReveal);   // first: content visibility depends on it
    safe('themeToggle', initThemeToggle);
    safe('mobileNav', initMobileNav);
    safe('searchToggle', initSearchToggle);
    safe('cartDrawer', initCartDrawer);
    safe('counters', initCounters);
    safe('faq', initFaq);
    safe('collectionFilters', initCollectionFilters);
    safe('scrollProgress', initScrollProgress);
    safe('productForm', initProductForm);
    safe('demoLauncher', initDemoLauncher);
    safe('quickAdd', initQuickAdd);
    safe('liveReadout', initLiveReadout);
    safe('playground', initPlayground);
    safe('compareSlider', initCompareSlider);
    safe('alarmConsole', initAlarmConsole);
    safe('paletteThemer', initPaletteThemer);
    safe('faqHub', initFaqHub);
  });

  /* ------------------------------------------------------------------ */
  /* FAQ hub — live search, category filtering, deep links               */
  /* ------------------------------------------------------------------ */
  function initFaqHub() {
    var root = qs('[data-faq-hub]');
    if (!root) return;

    var rows = qsa('[data-faq-row]', root);
    var search = qs('[data-faq-search]', root);
    var clearBtn = qs('[data-faq-clear]', root);
    var countEl = qs('[data-faq-count]', root);
    var emptyEl = qs('[data-faq-empty]', root);
    var toggleAll = qs('[data-faq-toggle-all]', root);
    var pills = qsa('[data-faq-cat]', root);
    if (!rows.length) return;

    var activeCat = 'all';
    var query = '';

    function open(row) {
      var panel = qs('[data-faq-panel]', row);
      var trigger = qs('[data-faq-trigger]', row);
      row.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }
    function close(row) {
      var panel = qs('[data-faq-panel]', row);
      var trigger = qs('[data-faq-trigger]', row);
      row.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.maxHeight = null;
    }

    // Unlike the homepage accordion, several answers can stay open here —
    // people comparing two answers shouldn't have one snap shut.
    rows.forEach(function (row) {
      var trigger = qs('[data-faq-trigger]', row);
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        row.classList.contains('is-open') ? close(row) : open(row);
        syncToggleAll();
      });
    });

    function syncToggleAll() {
      if (!toggleAll) return;
      var visible = rows.filter(function (r) { return !r.hidden; });
      var allOpen = visible.length > 0 && visible.every(function (r) { return r.classList.contains('is-open'); });
      toggleAll.textContent = allOpen ? 'Collapse all' : 'Expand all';
      toggleAll.setAttribute('aria-expanded', allOpen ? 'true' : 'false');
    }

    if (toggleAll) {
      toggleAll.addEventListener('click', function () {
        var visible = rows.filter(function (r) { return !r.hidden; });
        var allOpen = visible.every(function (r) { return r.classList.contains('is-open'); });
        visible.forEach(allOpen ? close : open);
        syncToggleAll();
      });
    }

    // --- highlighting -------------------------------------------------
    // Only ever wraps text nodes, and always unwraps before re-marking, so
    // repeated searches can't nest <mark> inside <mark>.
    function unmark(el) {
      qsa('mark.faq-hit', el).forEach(function (m) {
        m.replaceWith(document.createTextNode(m.textContent));
      });
      el.normalize();
    }
    function mark(el, term) {
      if (!term) return;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var targets = [], node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.toLowerCase().indexOf(term) !== -1) targets.push(node);
      }
      targets.forEach(function (textNode) {
        var frag = document.createDocumentFragment();
        var rest = textNode.nodeValue, idx;
        while ((idx = rest.toLowerCase().indexOf(term)) !== -1) {
          if (idx) frag.appendChild(document.createTextNode(rest.slice(0, idx)));
          var m = document.createElement('mark');
          m.className = 'faq-hit';
          m.textContent = rest.substr(idx, term.length);
          frag.appendChild(m);
          rest = rest.slice(idx + term.length);
        }
        if (rest) frag.appendChild(document.createTextNode(rest));
        textNode.replaceWith(frag);
      });
    }

    function apply() {
      var shown = 0;
      rows.forEach(function (row) {
        var matchesCat = activeCat === 'all' || row.getAttribute('data-cat') === activeCat;
        var matchesText = !query || (row.getAttribute('data-text') || '').indexOf(query) !== -1;
        var visible = matchesCat && matchesText;
        row.hidden = !visible;
        if (visible) shown++;

        var qText = qs('.faq-row-text', row);
        var aText = qs('.faq-row-a-in', row);
        [qText, aText].forEach(function (el) { if (el) unmark(el); });
        if (visible && query) {
          [qText, aText].forEach(function (el) { if (el) mark(el, query); });
          // a hit inside a collapsed answer is useless — open it
          if (!row.classList.contains('is-open') &&
              (row.getAttribute('data-text') || '').indexOf(query) !== -1) open(row);
        }
        // keep an open panel's height correct after markup changes
        if (row.classList.contains('is-open')) {
          var panel = qs('[data-faq-panel]', row);
          if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });

      if (countEl) countEl.textContent = shown + (shown === 1 ? ' answer' : ' answers');
      if (emptyEl) emptyEl.hidden = shown !== 0;
      if (clearBtn) clearBtn.hidden = !query;
      syncToggleAll();
    }

    if (search) {
      var t = null;
      search.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          query = search.value.trim().toLowerCase();
          apply();
        }, 120);
      });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        search.value = ''; query = ''; apply(); search.focus();
      });
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        activeCat = pill.getAttribute('data-faq-cat');
        pills.forEach(function (p) {
          var on = p === pill;
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        apply();
      });
    });

    // copy a permalink straight to one answer
    qsa('[data-faq-copy]', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = location.origin + location.pathname + btn.getAttribute('data-faq-copy');
        var done = function () {
          var prev = btn.textContent;
          btn.textContent = 'Link copied';
          btn.classList.add('is-copied');
          setTimeout(function () { btn.textContent = prev; btn.classList.remove('is-copied'); }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, function () {});
        } else {
          var ta = document.createElement('textarea');
          ta.value = url; document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); done(); } catch (e) {}
          document.body.removeChild(ta);
        }
      });
    });

    // arriving on a #q-... deep link should open and flag that answer
    function openFromHash() {
      if (!location.hash) return;
      var target = document.getElementById(location.hash.slice(1));
      if (!target || !target.hasAttribute('data-faq-row')) return;
      open(target);
      target.classList.add('is-target');
      setTimeout(function () { target.classList.remove('is-target'); }, 1800);
      target.scrollIntoView({ block: 'center' });
    }
    window.addEventListener('hashchange', openFromHash);

    apply();
    openFromHash();
  }

  window.addEventListener('load', function () {
    setTimeout(function () {
      var hidden = qsa('.reveal:not(.is-visible), .reveal-scale:not(.is-visible)').filter(function (el) {
        var r = el.getBoundingClientRect();
        return r.top < window.innerHeight && r.bottom > 0;   // in view but still hidden
      });
      if (hidden.length) forceReveal();
    }, 1200);
  });

  /* ------------------------------------------------------------------ */
  /* Palette themer — recolors the whole page from three accent tokens    */
  /* ------------------------------------------------------------------ */
  function initPaletteThemer() {
    var root = qs('[data-palette-themer]');
    if (!root) return;
    var buttons = qsa('[data-palette]', root);
    if (!buttons.length) return;

    // "soft" fills are just the accent at low alpha, so they can be derived
    // rather than specified per palette.
    function softOf(hex) {
      var h = hex.replace('#', '');
      if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
      var r = parseInt(h.substring(0, 2), 16),
          g = parseInt(h.substring(2, 4), 16),
          b = parseInt(h.substring(4, 6), 16);
      return 'rgba(' + r + ',' + g + ',' + b + ',0.12)';
    }

    function apply(btn) {
      var s = document.documentElement.style;
      var cyan = btn.getAttribute('data-cyan');
      var amber = btn.getAttribute('data-amber');
      var violet = btn.getAttribute('data-violet');

      // Gradients and every canvas read these same tokens, so setting the
      // three bases (plus their text-safe "ink" and low-alpha "soft"
      // variants) recolors the entire page — no per-component work.
      s.setProperty('--c-cyan', cyan);
      s.setProperty('--c-amber', amber);
      s.setProperty('--c-violet', violet);
      s.setProperty('--c-cyan-ink', btn.getAttribute('data-cyan-ink'));
      s.setProperty('--c-amber-ink', btn.getAttribute('data-amber-ink'));
      s.setProperty('--c-violet-ink', btn.getAttribute('data-violet-ink'));
      s.setProperty('--c-cyan-soft', softOf(cyan));
      s.setProperty('--c-amber-soft', softOf(amber));
      s.setProperty('--c-violet-soft', softOf(violet));

      buttons.forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () { apply(btn); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Before / after comparison slider                                     */
  /* ------------------------------------------------------------------ */
  function initCompareSlider() {
    qsa('[data-compare]').forEach(function (root) {
      var range = qs('[data-compare-range]', root);
      var after = qs('[data-compare-after]', root);
      var handle = qs('[data-compare-handle]', root);
      if (!range || !after) return;

      function apply() {
        var pct = parseFloat(range.value);
        // Reveal the "after" layer from the divider rightward.
        after.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
        if (handle) handle.style.left = pct + '%';
      }

      range.addEventListener('input', apply);
      // Pointer drag anywhere on the stage feels better than the native
      // thumb hit-area, which is only as wide as the thumb itself.
      var stage = qs('.compare-stage', root);
      if (stage) {
        var dragging = false;
        function fromEvent(e) {
          var rect = stage.getBoundingClientRect();
          var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
          range.value = Math.max(0, Math.min(100, (x / rect.width) * 100));
          apply();
        }
        stage.addEventListener('pointerdown', function (e) {
          dragging = true; stage.setPointerCapture(e.pointerId); fromEvent(e);
        });
        stage.addEventListener('pointermove', function (e) { if (dragging) fromEvent(e); });
        stage.addEventListener('pointerup', function (e) {
          dragging = false;
          try { stage.releasePointerCapture(e.pointerId); } catch (err) {}
        });
        stage.addEventListener('pointercancel', function () { dragging = false; });
      }
      apply();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Live alarm console — acknowledge, auto-return-to-normal, new events  */
  /* ------------------------------------------------------------------ */
  function initAlarmConsole() {
    var root = qs('[data-alarm-console]');
    if (!root) return;
    var list = qs('[data-alarm-list]', root);
    if (!list) return;

    var elActive = qs('[data-alarm-count-active]', root);
    var elUnacked = qs('[data-alarm-count-unacked]', root);
    var elToday = qs('[data-alarm-count-today]', root);
    var todayCount = parseInt((elToday && elToday.textContent) || '18', 10);

    var STATE_LABEL = { alarm: 'In alarm', acked: 'Acknowledged', normal: 'Normal' };

    // Events that can arrive while the visitor is watching. Kept short and
    // plausible — this is a product demo, not a simulator.
    var POOL = [
      { src: 'AHU-2', msg: 'Filter differential pressure high', pri: 'high' },
      { src: 'VAV-118', msg: 'Zone temp above cooling setpoint — 78.4°F', pri: 'med' },
      { src: 'BLR-1', msg: 'Boiler lockout — manual reset required', pri: 'crit' },
      { src: 'P-3', msg: 'Pump run status mismatch', pri: 'high' },
      { src: 'CT-1', msg: 'Cooling tower basin level low', pri: 'med' },
      { src: 'AHU-1', msg: 'Mixed air temp sensor out of range', pri: 'low' }
    ];
    var PRI_LABEL = { crit: 'Critical', high: 'High', med: 'Medium', low: 'Low' };
    var poolIdx = 0;

    function setState(row, state) {
      row.setAttribute('data-alarm-state', state);
      row.classList.remove('is-alarm', 'is-acked', 'is-normal');
      row.classList.add('is-' + state);
      var label = qs('[data-alarm-state-label]', row);
      if (label) label.textContent = STATE_LABEL[state];
      if (state !== 'alarm') {
        var btn = qs('[data-alarm-ack]', row);
        if (btn) btn.remove();
      }
      recount();
    }

    function recount() {
      var rows = qsa('[data-alarm]', list);
      var active = 0, unacked = 0;
      rows.forEach(function (r) {
        var st = r.getAttribute('data-alarm-state');
        if (st === 'alarm' || st === 'acked') active++;
        if (st === 'alarm') unacked++;
      });
      if (elActive) elActive.textContent = active;
      if (elUnacked) elUnacked.textContent = unacked;
      if (elToday) elToday.textContent = todayCount;
    }

    // Event delegation so rows added later work without rebinding.
    list.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-alarm-ack]') : null;
      if (!btn) return;
      var row = btn.closest('[data-alarm]');
      if (row) setState(row, 'acked');
    });

    var ackAll = qs('[data-alarm-ack-all]', root);
    if (ackAll) {
      ackAll.addEventListener('click', function () {
        qsa('[data-alarm]', list).forEach(function (row) {
          if (row.getAttribute('data-alarm-state') === 'alarm') setState(row, 'acked');
        });
      });
    }

    function stamp() {
      var d = new Date();
      return [d.getHours(), d.getMinutes(), d.getSeconds()]
        .map(function (n) { return String(n).padStart(2, '0'); }).join(':');
    }

    function addAlarm() {
      var a = POOL[poolIdx % POOL.length];
      poolIdx++;
      todayCount++;
      var li = document.createElement('li');
      li.className = 'alarm-row is-alarm is-new';
      li.setAttribute('data-alarm', '');
      li.setAttribute('data-alarm-state', 'alarm');
      li.innerHTML =
        '<span class="a-time">' + stamp() + '</span>' +
        '<span class="a-src"></span>' +
        '<span class="a-msg"></span>' +
        '<span class="a-pri"><em class="pri pri-' + a.pri + '">' + PRI_LABEL[a.pri] + '</em></span>' +
        '<span class="a-state" data-alarm-state-label>In alarm</span>' +
        '<span class="a-act"><button type="button" class="ack-btn" data-alarm-ack>Ack</button></span>';
      // textContent for the data-driven strings so a message containing
      // markup characters can never inject into the row.
      qs('.a-src', li).textContent = a.src;
      qs('.a-msg', li).textContent = a.msg;
      list.insertBefore(li, list.firstChild);
      setTimeout(function () { li.classList.remove('is-new'); }, 500);

      // Keep the list from growing without bound.
      var rows = qsa('[data-alarm]', list);
      if (rows.length > 6) list.removeChild(rows[rows.length - 1]);
      recount();
    }

    // Only run the live feed while the console is actually on screen and the
    // tab is visible — no point animating for nobody.
    var timer = null;
    function startFeed() {
      if (timer || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
      timer = setInterval(function () {
        if (document.hidden) return;
        // Retire an acknowledged alarm now and then, otherwise raise a new one.
        var acked = qsa('[data-alarm][data-alarm-state="acked"]', list);
        if (acked.length && Math.random() < 0.45) setState(acked[acked.length - 1], 'normal');
        else addAlarm();
      }, 9000);
    }
    function stopFeed() { if (timer) { clearInterval(timer); timer = null; } }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.isIntersecting ? startFeed() : stopFeed(); });
      }, { threshold: 0.2 }).observe(root);
    } else {
      startFeed();
    }

    recount();
  }

  /* ------------------------------------------------------------------ */
  /* Interactive playground — slider-driven live VAV graphic              */
  /* ------------------------------------------------------------------ */
  function initPlayground() {
    var slider = qs('[data-playground-slider]');
    var canvas = qs('#PlaygroundCanvas');
    if (!slider || !canvas) return;

    var zoneEl = qs('[data-pg-zone]');
    var damperEl = qs('[data-pg-damper]');
    var flowEl = qs('[data-pg-airflow]');
    var valveEl = qs('[data-pg-valve]');
    var datEl = qs('[data-pg-dat]');
    var modeEl = qs('[data-pg-mode]');
    var modeLabel = qs('[data-pg-mode-label]');
    var modeSub = qs('[data-pg-mode-sub]');

    var MODES = {
      cooling: {
        label: 'Cooling',
        color: '#3b82f6',
        sub: 'Damper opening for more 55°F primary air · reheat closed'
      },
      satisfied: {
        label: 'Satisfied',
        color: '#22c55e',
        sub: 'In deadband · minimum ventilation flow'
      },
      heating: {
        label: 'Heating',
        color: '#f97316',
        sub: 'Reheat valve modulating · airflow held at minimum first'
      }
    };

    function update() {
      var zoneTemp = parseFloat(slider.value);
      canvas.setAttribute('data-control-value', zoneTemp);

      // Same sequence the canvas draws — imported, not re-implemented, so the
      // numbers can never disagree with the picture.
      var api = window.NiagaraGraphics;
      if (!api || !api.vavSequence) return;
      var s = api.vavSequence(zoneTemp);

      if (zoneEl) zoneEl.textContent = zoneTemp.toFixed(1) + '°F';
      if (damperEl) damperEl.textContent = Math.round(s.damper * 100) + '%';
      if (flowEl) flowEl.textContent = Math.round(s.flow).toLocaleString() + ' CFM';
      if (valveEl) {
        valveEl.textContent = Math.round(s.valve * 100) + '%';
        valveEl.style.color = s.valve > 0.005 ? '#f97316' : '';
      }
      if (datEl) {
        datEl.textContent = Math.round(s.dat) + '°F';
        datEl.style.color = api.airColor(s.dat);
      }

      var m = MODES[s.mode];
      if (modeEl) modeEl.style.setProperty('--pg-mode-color', m.color);
      if (modeLabel) modeLabel.textContent = m.label;
      if (modeSub) modeSub.textContent = m.sub;
    }

    slider.addEventListener('input', update);
    // live-graphics.js loads with its own defer tag; if it hasn't defined the
    // shared sequence yet, retry once on load rather than rendering stale text.
    if (!window.NiagaraGraphics) window.addEventListener('load', update);
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Hero telemetry readout — cosmetic drifting value                    */
  /* ------------------------------------------------------------------ */
  function initLiveReadout() {
    var el = qs('[data-live-value]');
    if (!el || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
    var base = 55.4;
    setInterval(function () {
      var val = base + Math.sin(Date.now() / 4000) * 1.6 + (Math.random() - 0.5) * 0.3;
      el.textContent = val.toFixed(1) + '°F';
    }, 1200);
  }

  /* ------------------------------------------------------------------ */
  /* Light / dark theme toggle                                           */
  /* ------------------------------------------------------------------ */
  function initThemeToggle() {
    var btn = qs('[data-theme-toggle]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var root = document.documentElement;
      var isLight = root.getAttribute('data-theme') === 'light';
      var next = isLight ? 'dark' : 'light';
      if (next === 'light') root.setAttribute('data-theme', 'light');
      else root.removeAttribute('data-theme');
      try { localStorage.setItem('niagara-theme', next); } catch (e) {}
      document.dispatchEvent(new CustomEvent('niagara:theme-change', { detail: { theme: next } }));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Mobile nav                                                          */
  /* ------------------------------------------------------------------ */
  function initMobileNav() {
    var toggle = qs('[data-mobile-nav-toggle]');
    var nav = qs('[data-mobile-nav]');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      document.body.style.overflow = open ? 'hidden' : '';
    });
    qsa('a', nav).forEach(function (a) {
      a.addEventListener('click', function () {
        nav.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Header search panel                                                 */
  /* ------------------------------------------------------------------ */
  function initSearchToggle() {
    var btn = qs('[data-search-toggle]');
    var panel = qs('[data-search-panel]');
    if (!btn || !panel) return;
    btn.addEventListener('click', function () {
      var hidden = panel.hasAttribute('hidden');
      if (hidden) {
        panel.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
        var input = qs('input', panel);
        if (input) input.focus();
      } else {
        panel.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer + AJAX Cart API                                         */
  /* ------------------------------------------------------------------ */
  function initCartDrawer() {
    var drawer = qs('[data-cart-drawer]');
    var overlay = qs('[data-cart-overlay]');
    var openBtns = qsa('[data-cart-toggle]');
    var closeBtns = qsa('[data-cart-close]');
    if (!drawer) return;

    function open() {
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    openBtns.forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); open(); }); });
    closeBtns.forEach(function (b) { b.addEventListener('click', close); });
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

    window.NiagaraCart = { open: open, close: close, refresh: refreshDrawer };

    // Line qty +/- and remove, delegated
    document.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-line-minus]');
      var plus = e.target.closest('[data-line-plus]');
      var remove = e.target.closest('[data-line-remove]');
      if (minus || plus || remove) {
        var line = e.target.closest('[data-cart-line]');
        if (!line) return;
        var key = line.getAttribute('data-line-key');
        var input = qs('[data-line-qty]', line);
        var qty = parseInt(input.value, 10) || 1;
        if (minus) qty = Math.max(0, qty - 1);
        if (plus) qty = qty + 1;
        if (remove) qty = 0;
        updateLine(key, qty);
      }
    });
  }

  function updateLine(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { refreshDrawer(); })
      .catch(function () {});
  }

  function refreshDrawer() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        qsa('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
        var subtotal = qs('[data-cart-subtotal]');
        if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
        // Simplest reliable refresh: reload the drawer markup via the cart page section is heavier;
        // for a lightweight theme we just reload if the drawer body needs a full re-render.
        if (window.location.pathname === '/cart') {
          window.location.reload();
        } else {
          window.location.reload();
        }
      })
      .catch(function () {});
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ */
  /* Quick add (product cards) + product form (PDP)                      */
  /* ------------------------------------------------------------------ */
  function addToCart(variantId, qty, onDone) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty || 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        if (window.NiagaraCart) window.NiagaraCart.open();
        refreshDrawer();
        if (onDone) onDone();
      })
      .catch(function () { if (onDone) onDone(); });
  }

  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      btn.textContent = '···';
      addToCart(variantId, 1, function () { btn.innerHTML = '+'; });
    });
  }

  function initProductForm() {
    var form = qs('#ProductForm');
    if (!form) return;

    // qty stepper
    var minus = qs('[data-qty-minus]', form);
    var plus = qs('[data-qty-plus]', form);
    var qtyInput = qs('[data-qty-input]', form);
    if (minus && plus && qtyInput) {
      minus.addEventListener('click', function () { qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1); });
      plus.addEventListener('click', function () { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; });
    }

    // variant pill selection -> update hidden select
    var select = qs('[data-variant-select]', form);
    qsa('.variant-opts .pill', form).forEach(function (pill) {
      pill.addEventListener('click', function () {
        var group = pill.closest('.variant-opts');
        qsa('.pill', group).forEach(function (p) { p.classList.remove('is-active'); });
        pill.classList.add('is-active');
        var radio = qs('input', pill);
        if (radio) radio.checked = true;
      });
    });

    form.addEventListener('submit', function (e) {
      if (!select) return; // let native submit happen if no variant select
      e.preventDefault();
      var submitBtn = qs('button[type="submit"]', form);
      var original = submitBtn.textContent;
      submitBtn.textContent = 'Adding…';
      var variantId = select.value;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      addToCart(variantId, qty, function () { submitBtn.textContent = original; });
    });

    // gallery thumbs
    var mainImg = qs('#GalleryMainImg');
    qsa('[data-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        qsa('[data-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        if (mainImg) {
          mainImg.src = thumb.getAttribute('data-src');
          var thumbImg = qs('img', thumb);
          if (thumbImg) mainImg.alt = thumbImg.alt;
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /* ------------------------------------------------------------------ */
  function initScrollReveal() {
    var items = qsa('.reveal, .reveal-scale');
    if (!items.length) return;

    function show(el) { el.classList.add('is-visible'); }

    // Reveal anything that has reached the viewport. Returns true while some
    // elements are still below the fold and waiting their turn.
    function sweep() {
      var pending = false;
      for (var i = 0; i < items.length; i++) {
        var el = items[i];
        if (el.classList.contains('is-visible')) continue;
        if (el.getBoundingClientRect().top < window.innerHeight * 0.98) show(el);
        else pending = true;
      }
      return pending;
    }

    sweep();   // whatever is already on screen at load shows straight away

    if (!('IntersectionObserver' in window)) {
      items.forEach(show);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          show(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
    items.forEach(function (el) { if (!el.classList.contains('is-visible')) io.observe(el); });

    // A flick scroll, a scrollbar drag, an in-page anchor or a restored scroll
    // position can move an element clean through the viewport between two
    // observer samples — it then never reports as intersecting and stays
    // invisible for good. This throttled sweep is the guarantee that anything
    // scrolled past actually gets shown; it unhooks itself once all are done.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!sweep()) window.removeEventListener('scroll', onScroll);
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Animated counters                                                    */
  /* ------------------------------------------------------------------ */
  function initCounters() {
    var counters = qsa('[data-counter]');
    if (!counters.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        animateCounter(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.4 });
    counters.forEach(function (el) { io.observe(el); });
  }

  function animateCounter(el) {
    var target = parseFloat(el.getAttribute('data-target')) || 0;
    var decimals = parseInt(el.getAttribute('data-decimals'), 10) || 0;
    var duration = 1400;
    var start = performance.now();
    function tick(now) {
      var p = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      var val = target * eased;
      el.textContent = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString();
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = decimals ? target.toFixed(decimals) : target.toLocaleString();
    }
    requestAnimationFrame(tick);
  }

  /* ------------------------------------------------------------------ */
  /* FAQ accordion                                                        */
  /* ------------------------------------------------------------------ */
  function initFaq() {
    qsa('[data-faq-item]').forEach(function (item) {
      var trigger = qs('[data-faq-trigger]', item);
      var panel = qs('[data-faq-panel]', item);
      if (!trigger || !panel) return;
      trigger.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        qsa('[data-faq-item]').forEach(function (other) {
          other.classList.remove('is-open');
          qs('[data-faq-trigger]', other).setAttribute('aria-expanded', 'false');
          qs('[data-faq-panel]', other).style.maxHeight = null;
        });
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Collection filter bar: instant search + sort navigation              */
  /* ------------------------------------------------------------------ */
  function initCollectionFilters() {
    var searchInput = qs('[data-collection-search]');
    var grid = qs('[data-product-grid]');
    var emptyState = qs('[data-empty-state]');
    var resultCount = qs('[data-result-count]');
    var clearBtn = qs('[data-clear-search]');

    if (searchInput && grid) {
      var cards = qsa('[data-product-title]', grid);
      searchInput.addEventListener('input', function () {
        var term = searchInput.value.trim().toLowerCase();
        var visible = 0;
        cards.forEach(function (card) {
          var title = card.getAttribute('data-product-title') || '';
          var match = term === '' || title.indexOf(term) !== -1;
          card.style.display = match ? '' : 'none';
          if (match) visible++;
        });
        if (emptyState) emptyState.hidden = visible !== 0;
        grid.style.display = visible === 0 ? 'none' : '';
        if (resultCount) resultCount.textContent = visible + (visible === 1 ? ' graphic' : ' graphics');
      });
    }

    if (clearBtn && searchInput) {
      clearBtn.addEventListener('click', function () {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input'));
      });
    }

    var sortSelect = qs('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.href = url.toString();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Scroll progress bar                                                  */
  /* ------------------------------------------------------------------ */
  function initScrollProgress() {
    var bar = qs('[data-scroll-progress]');
    if (!bar) return;
    function update() {
      var h = document.documentElement;
      var scrollTop = h.scrollTop || document.body.scrollTop;
      var height = h.scrollHeight - h.clientHeight;
      var pct = height > 0 ? (scrollTop / height) * 100 : 0;
      bar.style.width = pct + '%';
    }
    document.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Live .html demo launcher                                           */
  /* ------------------------------------------------------------------ */

  // The "Live preview" link on a product page points at assets/demo.html and
  // already carries target="_blank". This upgrades it to a real, sized, named
  // window so the demo sits beside the store instead of being buried in
  // another tab. window.open runs inside the click gesture, so popup blockers
  // allow it; if one still refuses (open returns null) we skip preventDefault
  // and the plain target="_blank" takes over.
  function initDemoLauncher() {
    var links = qsa('[data-demo-launch]');
    if (!links.length) return;

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        // Modifier-clicks and middle-clicks belong to the browser.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

        var availW = window.screen && window.screen.availWidth ? window.screen.availWidth : 1280;
        var availH = window.screen && window.screen.availHeight ? window.screen.availHeight : 800;
        var w = Math.max(360, Math.min(1180, availW - 120));
        var h = Math.max(320, Math.min(820, availH - 140));
        var features = [
          'popup=yes',
          'width=' + w,
          'height=' + h,
          'left=' + Math.max(0, Math.round((availW - w) / 2)),
          'top=' + Math.max(0, Math.round((availH - h) / 2)),
          'resizable=yes',
          'scrollbars=yes'
        ].join(',');
        var name = 'niagara-demo-' + (link.getAttribute('data-demo-key') || 'preview');

        var win = window.open(link.href, name, features);
        if (!win) return; // blocked - let target="_blank" handle it

        e.preventDefault();
        // Cross-origin (the demo is served from the theme asset CDN), so this
        // throws in most browsers; rel="noopener" on the link is the real guard.
        try { win.opener = null; } catch (err) {}
        win.focus();
      });
    });
  }

})();

/* ---------------------------------------------------------------
   PRODUCT GALLERY LIGHTBOX - click to enlarge, wheel / pinch zoom
   --------------------------------------------------------------- */
(function () {
  "use strict";

  var gallery = document.querySelector("[data-gallery-main]");
  if (!gallery) { return; }
  var mainImg = gallery.querySelector("img");
  if (!mainImg) { return; }

  var MIN = 1, MAX = 6, STEP = 1.45;

  var slides = [];
  var index = 0;
  var scale = 1, tx = 0, ty = 0;

  var box = null, stage = null, img = null, counter = null, caption = null;
  var prevBtn = null, nextBtn = null, outBtn = null, closeBtn = null;
  var built = false, lastFocus = null;
  var pointers = [], pinchDist = 0, pinchBase = 1;
  var dragging = false, dragX = 0, dragY = 0, dragMoved = false;

  function fileKey(url) {
    if (!url) { return ""; }
    var f = url.split("?")[0].split("/").pop();
    return f.replace(/_\d+x\d*(?=\.)/, "");
  }
  function same(a, b) { return fileKey(a) === fileKey(b); }

  function fromDom() {
    var out = [], thumbs = document.querySelectorAll("[data-thumb]"), i, t, im;
    for (i = 0; i < thumbs.length; i++) {
      t = thumbs[i];
      im = t.querySelector("img");
      out.push({
        src: t.getAttribute("data-src"),
        alt: (im && im.getAttribute("alt")) || mainImg.getAttribute("alt") || ""
      });
    }
    if (!out.length) {
      out.push({ src: mainImg.getAttribute("src"), alt: mainImg.getAttribute("alt") || "" });
    }
    return out;
  }

  /* The thumb row only renders a few images - pull the full ordered set. */
  function enrich() {
    var path = window.location.pathname.replace(/\/+$/, "");
    if (path.indexOf("/products/") === -1 || !window.fetch) { return; }
    fetch(path + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.images || !data.images.length) { return; }
        var base = mainImg.getAttribute("alt") || data.title || "";
        var next = [], i;
        for (i = 0; i < data.images.length; i++) {
          next.push({ src: data.images[i], alt: base + " \u2014 view " + (i + 1) + " of " + data.images.length });
        }
        slides = next;
      })
      .catch(function () {});
  }

  function startIndex() {
    var src = mainImg.getAttribute("src"), i;
    for (i = 0; i < slides.length; i++) { if (same(slides[i].src, src)) { return i; } }
    return 0;
  }

  function icon(d) {
    return "<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'>" + d + "</svg>";
  }

  function button(cls, label, path) {
    var b = document.createElement("button");
    b.type = "button";
    b.className = cls;
    b.setAttribute("aria-label", label);
    b.innerHTML = icon(path);
    return b;
  }

  function apply(animate) {
    stage.classList.toggle("is-interacting", !animate);
    img.style.transform = "translate(" + tx + "px," + ty + "px) scale(" + scale + ")";
    stage.classList.toggle("is-zoomed", scale > 1.01);
    if (outBtn) { outBtn.disabled = scale <= MIN + 0.001; }
  }

  function clampPan() {
    var w = img.offsetWidth * scale, h = img.offsetHeight * scale;
    var mx = Math.max(0, (w - stage.clientWidth) / 2);
    var my = Math.max(0, (h - stage.clientHeight) / 2);
    if (tx > mx) { tx = mx; }
    if (tx < -mx) { tx = -mx; }
    if (ty > my) { ty = my; }
    if (ty < -my) { ty = -my; }
  }

  function zoomAt(target, px, py, animate) {
    var s = Math.min(MAX, Math.max(MIN, target));
    if (Math.abs(s - scale) < 0.0005) { return; }
    var r = img.getBoundingClientRect();
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    var k = s / scale;
    tx += (px - cx) * (1 - k);
    ty += (py - cy) * (1 - k);
    scale = s;
    if (scale <= MIN + 0.001) { scale = MIN; tx = 0; ty = 0; }
    clampPan();
    apply(animate);
  }

  function zoomBy(f) {
    var r = stage.getBoundingClientRect();
    zoomAt(scale * f, r.left + r.width / 2, r.top + r.height / 2, true);
  }

  function resetZoom() { scale = MIN; tx = 0; ty = 0; apply(true); }

  function build() {
    box = document.createElement("div");
    box.className = "lightbox";
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", "Product image viewer");

    stage = document.createElement("div");
    stage.className = "lightbox-stage";
    img = document.createElement("img");
    img.className = "lightbox-img";
    img.alt = "";
    img.draggable = false;
    stage.appendChild(img);

    var bar = document.createElement("div");
    bar.className = "lightbox-bar";
    counter = document.createElement("span");
    counter.className = "lightbox-count";
    bar.appendChild(counter);

    var inBtn = button("lightbox-btn", "Zoom in", "<path d='M12 5v14M5 12h14'/>");
    outBtn = button("lightbox-btn", "Zoom out", "<path d='M5 12h14'/>");
    var rstBtn = button("lightbox-btn", "Reset zoom", "<path d='M3 12a9 9 0 1 0 3-6.7'/><path d='M3 4.2V9h4.8'/>");
    closeBtn = button("lightbox-btn", "Close viewer", "<path d='M6 6l12 12M18 6L6 18'/>");
    bar.appendChild(inBtn);
    bar.appendChild(outBtn);
    bar.appendChild(rstBtn);
    bar.appendChild(closeBtn);

    prevBtn = button("lightbox-btn lightbox-nav is-prev", "Previous image", "<path d='M15 5l-7 7 7 7'/>");
    nextBtn = button("lightbox-btn lightbox-nav is-next", "Next image", "<path d='M9 5l7 7-7 7'/>");

    caption = document.createElement("p");
    caption.className = "lightbox-caption";

    box.appendChild(stage);
    box.appendChild(bar);
    box.appendChild(prevBtn);
    box.appendChild(nextBtn);
    box.appendChild(caption);
    document.body.appendChild(box);

    inBtn.addEventListener("click", function () { zoomBy(STEP); });
    outBtn.addEventListener("click", function () { zoomBy(1 / STEP); });
    rstBtn.addEventListener("click", resetZoom);
    closeBtn.addEventListener("click", close);
    prevBtn.addEventListener("click", function () { go(-1); });
    nextBtn.addEventListener("click", function () { go(1); });

    stage.addEventListener("click", onStageClick);
    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("pointerdown", onDown);
    stage.addEventListener("pointermove", onMove);
    stage.addEventListener("pointerup", onUp);
    stage.addEventListener("pointercancel", onUp);
    img.addEventListener("load", function () { box.classList.remove("is-loading"); });
    document.addEventListener("keydown", onKey);
    built = true;
  }

  function findPointer(id) {
    for (var i = 0; i < pointers.length; i++) { if (pointers[i].id === id) { return i; } }
    return -1;
  }

  function dist(a, b) {
    var dx = a.x - b.x, dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function onWheel(e) {
    e.preventDefault();
    zoomAt(scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12), e.clientX, e.clientY, false);
  }

  function onStageClick(e) {
    if (dragMoved) { dragMoved = false; return; }
    if (e.target !== img) { close(); return; }
    if (scale > 1.01) { resetZoom(); } else { zoomAt(2.6, e.clientX, e.clientY, true); }
  }

  function onDown(e) {
    pointers.push({ id: e.pointerId, x: e.clientX, y: e.clientY });
    if (pointers.length === 2) {
      pinchDist = dist(pointers[0], pointers[1]);
      pinchBase = scale;
      dragging = false;
      return;
    }
    if (scale > 1.01) {
      dragging = true;
      dragMoved = false;
      dragX = e.clientX - tx;
      dragY = e.clientY - ty;
      stage.classList.add("is-panning");
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    }
  }

  function onMove(e) {
    var i = findPointer(e.pointerId);
    if (i > -1) { pointers[i].x = e.clientX; pointers[i].y = e.clientY; }
    if (pointers.length === 2 && pinchDist) {
      e.preventDefault();
      var d = dist(pointers[0], pointers[1]);
      zoomAt(pinchBase * (d / pinchDist), (pointers[0].x + pointers[1].x) / 2, (pointers[0].y + pointers[1].y) / 2, false);
      return;
    }
    if (!dragging) { return; }
    e.preventDefault();
    tx = e.clientX - dragX;
    ty = e.clientY - dragY;
    dragMoved = true;
    clampPan();
    apply(false);
  }

  function onUp(e) {
    var i = findPointer(e.pointerId);
    if (i > -1) { pointers.splice(i, 1); }
    if (pointers.length < 2) { pinchDist = 0; }
    if (dragging && !pointers.length) {
      dragging = false;
      stage.classList.remove("is-panning");
      try { stage.releasePointerCapture(e.pointerId); } catch (err) {}
    }
  }

  function onKey(e) {
    if (!box || !box.classList.contains("is-open")) { return; }
    var k = e.key;
    if (k === "Escape") { e.preventDefault(); close(); }
    else if (k === "ArrowRight") { e.preventDefault(); go(1); }
    else if (k === "ArrowLeft") { e.preventDefault(); go(-1); }
    else if (k === "+" || k === "=") { e.preventDefault(); zoomBy(STEP); }
    else if (k === "-" || k === "_") { e.preventDefault(); zoomBy(1 / STEP); }
    else if (k === "0") { e.preventDefault(); resetZoom(); }
    else if (k === "Tab") {
      var f = box.querySelectorAll(".lightbox-btn:not([hidden]):not([disabled])");
      if (!f.length) { return; }
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  function preload(i) {
    if (slides.length < 2) { return; }
    var s = slides[(i + slides.length) % slides.length];
    if (s && s.src) { var p = new Image(); p.src = s.src; }
  }

  function show(i) {
    if (!slides.length) { return; }
    index = (i + slides.length) % slides.length;
    var s = slides[index];
    scale = MIN; tx = 0; ty = 0;
    box.classList.add("is-loading");
    img.src = s.src;
    img.alt = s.alt || "";
    caption.textContent = s.alt || "";
    counter.textContent = (index + 1) + " / " + slides.length;
    prevBtn.hidden = slides.length < 2;
    nextBtn.hidden = slides.length < 2;
    apply(false);
    preload(index + 1);
    preload(index - 1);
  }

  function go(d) { show(index + d); }

  function open() {
    if (!built) { build(); }
    if (!slides.length) { slides = fromDom(); }
    lastFocus = document.activeElement;
    show(startIndex());
    box.classList.add("is-open");
    document.body.classList.add("lightbox-open");
    window.setTimeout(function () { closeBtn.focus(); }, 60);
  }

  function close() {
    if (!box) { return; }
    box.classList.remove("is-open");
    document.body.classList.remove("lightbox-open");
    resetZoom();
    if (lastFocus && lastFocus.focus) { lastFocus.focus(); }
  }

  var hint = document.createElement("span");
  hint.className = "gallery-zoom-hint";
  hint.innerHTML = icon("<circle cx='11' cy='11' r='7'/><path d='M20 20l-3.4-3.4M11 8.4v5.2M8.4 11h5.2'/>") + "<span>Click to enlarge</span>";
  gallery.appendChild(hint);

  mainImg.setAttribute("role", "button");
  mainImg.setAttribute("tabindex", "0");
  mainImg.setAttribute("aria-haspopup", "dialog");
  if (!mainImg.getAttribute("title")) { mainImg.setAttribute("title", "Click to enlarge"); }

  mainImg.addEventListener("click", function (e) {
    if (gallery.classList.contains("is-live")) { return; }
    e.preventDefault();
    open();
  });

  mainImg.addEventListener("keydown", function (e) {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") { e.preventDefault(); open(); }
  });

  slides = fromDom();
  enrich();
})();
